import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { 
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell 
} from 'recharts';
import { db } from '../../lib/firebase';
import Spinner from '../../components/Spinner';

const ExamAnalytics = ({ exam, onBack, setModal }) => {
    const [stats, setStats] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [totalAttempts, setTotalAttempts] = useState(0);

    // Use published title if available, otherwise fallback to standard title
    const displayTitle = exam.publishedTitle || exam.title;

    useEffect(() => {
        const fetchStats = async () => {
            try {
                // Fetch all submitted attempts for this exam
                const q = query(
                    collection(db, "attempts"),
                    where("examId", "==", exam.id),
                    where("submittedAt", "!=", null)
                );
                const snapshot = await getDocs(q);
                const attempts = snapshot.docs.map(doc => doc.data());
                
                setTotalAttempts(attempts.length);

                if (attempts.length === 0) {
                    setIsLoading(false);
                    return;
                }

                // --- NEW VERSIONING LOGIC: Determine where to get the questions ---
                let questionsToAnalyze = [];
                if (exam.questions && Array.isArray(exam.questions) && exam.questions.length > 0) {
                    // Legacy system: questions are directly on the document
                    questionsToAnalyze = exam.questions;
                } else {
                    // New system: questions are in Firebase Storage
                    const targetUrl = exam.publishedQuestionsUrl || exam.questionsUrl;
                    if (targetUrl) {
                        try {
                            const res = await fetch(targetUrl);
                            if (res.ok) {
                                const fetchedData = await res.json();
                                if (Array.isArray(fetchedData)) {
                                    questionsToAnalyze = fetchedData;
                                }
                            } else {
                                console.error("Failed to fetch questions JSON, HTTP status:", res.status);
                            }
                        } catch (err) {
                            console.error("Error fetching questions JSON for analytics:", err);
                        }
                    }
                }

                if (questionsToAnalyze.length === 0) {
                    console.warn("No questions found to analyze for this exam.");
                    setIsLoading(false);
                    return;
                }

                // --- FLAT STATS BUILDER (Handles standard & nested case-study questions) ---
                const flatQuestionStats = [];

                const processQuestion = (q, qIndex, subIndex = null) => {
                    // Skip descriptive questions as they cannot be strictly auto-graded for a pass/fail rate chart
                    if (q.type === 'descriptive') return;

                    let incorrectCount = 0;
                    let skippedCount = 0;

                    attempts.forEach(attempt => {
                        let answer;
                        
                        // Extract answer based on whether it is a sub-question or main question
                        if (subIndex !== null) {
                            answer = attempt.answers && attempt.answers[qIndex] ? attempt.answers[qIndex][subIndex] : undefined;
                        } else {
                            answer = attempt.answers ? attempt.answers[qIndex] : undefined;
                        }
                        
                        // Check if skipped (undefined or empty)
                        if (answer === undefined || answer === null || answer === '') {
                            skippedCount++;
                            // We count skipped as incorrect for the purpose of "getting it wrong"
                            incorrectCount++; 
                            return;
                        }

                        // Check correctness based on type
                        let isCorrect = false;

                        if (q.type === 'mcq') {
                            const correctArr = [...(q.correctAnswer || [])].sort();
                            const ansArr = Array.isArray(answer) ? [...answer].sort() : [answer];
                            isCorrect = JSON.stringify(correctArr) === JSON.stringify(ansArr);
                        } else if (q.type === 'dropdown') {
                            isCorrect = Number(answer) === Number(q.correctAnswer);
                        } else if (q.type === 'short-answer') {
                            isCorrect = String(answer).trim().toLowerCase() === String(q.correctAnswer || '').trim().toLowerCase();
                        } else if (q.type === 'matching') {
                            const correctObj = q.correctAnswer || {};
                            const ansObj = answer || {};
                            const keys = Object.keys(correctObj);
                            // Must match length and all values
                            if (keys.length > 0 && keys.length === Object.keys(ansObj).length) {
                                isCorrect = keys.every(k => Number(correctObj[k]) === Number(ansObj[k]));
                            }
                        } else if (q.type === 'mcq-grid') {
                            // Simplified check for grid (if all rows match)
                            const correctObj = q.correctAnswer || {};
                            const ansObj = answer || {};
                            const rows = q.rows || [];
                            if (rows.length > 0) {
                                isCorrect = rows.every((_, rIdx) => Number(correctObj[rIdx]) === Number(ansObj[rIdx]));
                            }
                        }

                        if (!isCorrect) {
                            incorrectCount++;
                        }
                    });

                    // Strip HTML tags for a clean table/tooltip preview
                    const rawText = q.text ? q.text.replace(/<[^>]*>?/gm, '').trim() : '';
                    const previewText = rawText.substring(0, 40) + (rawText.length > 40 ? '...' : '');

                    // ID formatting (e.g., "Q1" for normal, "Q1.2" for case-study sub-question 2)
                    let qId = `Q${qIndex + 1}`;
                    if (subIndex !== null) {
                        qId = `Q${qIndex + 1}.${subIndex + 1}`;
                    }

                    flatQuestionStats.push({
                        id: qId,
                        questionText: previewText || `${qId} (Image Based or No Text)`,
                        type: q.type,
                        incorrectCount: incorrectCount,
                        incorrectRate: Math.round((incorrectCount / attempts.length) * 100),
                        skippedCount: skippedCount
                    });
                };

                // Loop over the questions to feed into the processor
                questionsToAnalyze.forEach((question, index) => {
                    if (question.type === 'case-study') {
                        if (question.subQuestions && Array.isArray(question.subQuestions)) {
                            question.subQuestions.forEach((subQ, subIdx) => {
                                processQuestion(subQ, index, subIdx);
                            });
                        }
                    } else {
                        processQuestion(question, index);
                    }
                });

                setStats(flatQuestionStats);
            } catch (error) {
                console.error("Error calculating analytics:", error);
                setModal({ show: true, message: "Failed to load analytics data." });
            } finally {
                setIsLoading(false);
            }
        };

        if (exam) {
            fetchStats();
        }
    }, [exam, setModal]);

    if (isLoading) return <div className="flex justify-center mt-8"><Spinner color="border-purple-500" /></div>;

    return (
        <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex justify-between items-center mb-6">
                <div>
                    <button onClick={onBack} className="text-sm text-purple-600 hover:underline mb-2">&larr; Back to Exam List</button>
                    <h2 className="text-2xl font-bold text-gray-800">Analytics: {displayTitle}</h2>
                    <p className="text-gray-600">Total Attempts: {totalAttempts}</p>
                </div>
            </div>

            {totalAttempts === 0 ? (
                <div className="p-8 text-center text-gray-500 bg-gray-50 rounded-lg">
                    No attempts have been made for this exam yet.
                </div>
            ) : (
                <div className="space-y-8">
                    {/* Chart Section */}
                    <div className="h-80 w-full bg-gray-50 p-4 rounded-lg border">
                        <h3 className="text-lg font-bold mb-4 text-gray-700">Incorrect Response Rate by Question</h3>
                        <ResponsiveContainer width="100%" height="90%">
                            <BarChart data={stats} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                <XAxis dataKey="id" />
                                <YAxis label={{ value: '% Incorrect', angle: -90, position: 'insideLeft' }} />
                                <Tooltip 
                                    content={({ active, payload, label }) => {
                                        if (active && payload && payload.length) {
                                            const data = payload[0].payload;
                                            return (
                                                <div className="bg-white p-3 border rounded shadow-lg">
                                                    <p className="font-bold">{label} ({data.type})</p>
                                                    <p className="text-sm text-gray-600">{data.questionText}</p>
                                                    <p className="text-red-600 font-semibold mt-1">Incorrect: {data.incorrectRate}%</p>
                                                    <p className="text-xs text-gray-500">({data.incorrectCount} out of {totalAttempts} students)</p>
                                                </div>
                                            );
                                        }
                                        return null;
                                    }}
                                />
                                <Bar dataKey="incorrectRate" name="Incorrect %" fill="#ef4444" radius={[4, 4, 0, 0]}>
                                    {stats.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={entry.incorrectRate > 50 ? '#b91c1c' : '#ef4444'} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>

                    {/* Table Section */}
                    <div className="overflow-x-auto">
                        <h3 className="text-lg font-bold mb-4 text-gray-700">Detailed Breakdown</h3>
                        <table className="min-w-full divide-y divide-gray-200 border">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Q#</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Question Preview</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Incorrect Count</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Incorrect Rate</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {stats.map((row, idx) => (
                                    <tr key={idx} className={row.incorrectRate > 50 ? "bg-red-50" : ""}>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">{row.id}</td>
                                        <td className="px-6 py-4 text-sm text-gray-600 max-w-xs truncate" title={row.questionText}>
                                            {row.questionText}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 capitalize">{row.type.replace('-', ' ')}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{row.incorrectCount} / {totalAttempts}</td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${row.incorrectRate > 50 ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}`}>
                                                {row.incorrectRate}%
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};
export default ExamAnalytics;